import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { Logger } from "shared/lib/Logger"
import { getBuildingTypeIcon } from "shared/lib/building-references/getBuildingTypeIcon"
import { useEvent } from "shared/lib/useEvent"

import {
  ProductFilterItem,
  ProductFilterType
} from "widgets/product-filter/types/ProductFilterType"

import { ProductFilterChipListProps } from "../ProductFilterChipList"
import {
  OnRemoveProductFilter,
  ProductFilterChipListViewModel
} from "../ProductFilterChipListView"

export function useProductFilterChipListViewModel({
  filterState,
  productGroupOptions,
  onChange
}: ProductFilterChipListProps): ProductFilterChipListViewModel {
  const { t } = useTranslation()

  const handleRemove: OnRemoveProductFilter<ProductFilterType> = useEvent(
    (filterType, value) => {
      const newState = { ...filterState }

      switch (filterType) {
        case ProductFilterType.PRODUCT_GROUP:
          newState.productGroups =
            filterState.productGroups?.filter(
              productGroup => productGroup !== value
            ) || []
          break
        case ProductFilterType.ROOM_TYPE:
          newState.roomTypes =
            filterState.roomTypes?.filter(roomType => roomType !== value) || []
          break
        case ProductFilterType.BUILDING_TYPE:
          newState.buildingTypes =
            filterState.buildingTypes?.filter(
              buildingType => buildingType !== value
            ) || []
          break
        default:
          Logger.warn(
            "UnknownFilterError",
            `cannot remove filter of type: ${filterType} `
          )
          break
      }

      onChange?.(newState)
    }
  )

  const chips = useMemo<ProductFilterItem[]>(() => {
    const result: ProductFilterItem[] = []

    if (filterState.productGroups?.length && productGroupOptions) {
      filterState.productGroups.forEach(productGroupId => {
        const productGroupOption = productGroupOptions?.find(
          opt => opt.value === productGroupId
        )
        if (productGroupOption) {
          result.push({
            key: `product-group-${productGroupId}`,
            label: productGroupOption.label,
            image: productGroupOption.image,
            productGroupObj: productGroupOption.data,
            filterType: ProductFilterType.PRODUCT_GROUP,
            value: productGroupId
          })
        }
      })
    }

    if (filterState.buildingTypes?.length) {
      filterState.buildingTypes.forEach(buildingType => {
        result.push({
          key: `building-type-${buildingType}`,
          label: t(`BuildingReference.BuildingType.${buildingType}`) as string,
          icon: getBuildingTypeIcon(buildingType),
          filterType: ProductFilterType.BUILDING_TYPE,
          value: buildingType
        })
      })
    }

    if (filterState.roomTypes?.length) {
      filterState.roomTypes.forEach(roomType => {
        result.push({
          key: `room-type-${roomType}`,
          label: t(`RoomType.${roomType}`) as string,
          filterType: ProductFilterType.ROOM_TYPE,
          value: roomType
        })
      })
    }
    return result
  }, [filterState, productGroupOptions, t])

  return {
    chips,
    onRemove: handleRemove
  }
}
